import { Button, Drawer, DropdownMenu, Input, Prompt, toast } from "@medusajs/ui"
import { useQueryClient } from "@tanstack/react-query"
import { useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { Field } from "@/components/form"
import { DataTable, EmptyState, ErrorState, FileThumb, PageHeader, SearchInput, Section, usePage } from "@/components/ui"
import { formatDate } from "@/lib/format"
import { ACCEPT, FORMATS, fileSize, formatOf, isImage, uploadMany, useDeleteFiles, useFiles, useFileUsage, useUpdateFile, type MediaFile } from "@/lib/files"
import { useCan } from "@/lib/permissions"
import { errorMessage } from "@/lib/sdk"

const PAGE = 50

function UsageList({ ids }: { ids: string[] }) {
  const { data } = useFileUsage(ids)
  const rows = Object.entries(data?.usage ?? {}).flatMap(([id, u]) => u.map((x) => ({ ...x, id })))
  if (!rows.length) return null
  return (
    <div className="rounded-lg border border-ui-border-base bg-ui-bg-subtle p-3 text-[13px]">
      <div className="font-medium">Used on your store</div>
      <ul className="mt-1 list-inside list-disc text-ui-fg-subtle">
        {rows.slice(0, 8).map((r, i) => <li key={i}>{r.type}: {r.title}</li>)}
      </ul>
      <div className="mt-1 text-ui-fg-subtle">Deleting it will break these images.</div>
    </div>
  )
}

function FileDrawer({ file, onClose, onDelete, writable }: { file: MediaFile; onClose: () => void; onDelete: () => void; writable: boolean }) {
  const [alt, setAlt] = useState(file.alt ?? "")
  const [name, setName] = useState(file.filename.replace(/\.[^.]*$/, ""))
  const save = useUpdateFile()
  const dirty = alt !== (file.alt ?? "") || name !== file.filename.replace(/\.[^.]*$/, "")
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(file.url)
      toast.success("Link copied")
    } catch {
      toast.error("Couldn't copy. Select the link and copy it.")
    }
  }
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <Drawer.Content className="max-w-[480px]">
        <Drawer.Header><Drawer.Title className="truncate">{file.filename}</Drawer.Title></Drawer.Header>
        <Drawer.Body className="flex flex-col gap-y-4 overflow-y-auto">
          <div className="flex items-center justify-center overflow-hidden rounded-lg border bg-ui-bg-subtle">
            {isImage(file) ? <img src={file.url} alt={file.alt ?? ""} className="max-h-[320px] w-full object-contain" /> : file.mime_type.startsWith("video/") ? <video src={file.url} controls className="max-h-[320px] w-full" /> : <div className="py-16 text-[13px] text-ui-fg-subtle">{file.mime_type}</div>}
          </div>
          <div className="text-[12px] text-ui-fg-subtle">
            {fileSize(file.size)}{file.width && file.height ? ` · ${file.width} × ${file.height}` : ""} · {file.mime_type} · Added {formatDate(file.created_at)}
          </div>
          <Field label="Link">
            <div className="flex gap-x-2">
              <Input readOnly value={file.url} onFocus={(e) => e.currentTarget.select()} />
              <Button size="small" variant="secondary" onClick={copy}>Copy</Button>
            </div>
          </Field>
          {writable && (
            <>
              <Field label="File name"><Input value={name} maxLength={100} onChange={(e) => setName(e.target.value)} /></Field>
              {isImage(file) && <Field label="Alt text" hint="Describe the image for screen readers and search engines."><Input value={alt} maxLength={512} onChange={(e) => setAlt(e.target.value)} /></Field>}
            </>
          )}
          <UsageList ids={[file.id]} />
        </Drawer.Body>
        <Drawer.Footer>
          {writable && <Button size="small" variant="danger" className="mr-auto" onClick={onDelete}>Delete</Button>}
          <Button size="small" variant="secondary" onClick={onClose}>Close</Button>
          {writable && (
            <Button
              size="small"
              disabled={!dirty || !name.trim()}
              isLoading={save.isPending}
              onClick={() => save.mutate({ id: file.id, filename: name, alt: isImage(file) ? alt : undefined }, { onSuccess: () => { toast.success("Saved"); onClose() }, onError: (e) => toast.error(errorMessage(e)) })}
            >
              Save
            </Button>
          )}
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  )
}

const TYPE_LABEL: Record<string, string> = { image: "Image", video: "Video", pdf: "PDF" }
const SIZE_LABEL: Record<string, string> = { small: "Under 1 MB", medium: "1–5 MB", large: "Over 5 MB" }
const ALT_LABEL: Record<string, string> = { has: "Has alt text", missing: "Missing alt text" }
const SORTS = [["created_at", "Date added"], ["filename", "File name"], ["size", "Size"], ["format", "File format"]] as const
type Col = "alt" | "date" | "size"
const COLS: [Col, string][] = [["alt", "Alt text"], ["date", "Date added"], ["size", "Size"]]

function Chip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-x-1 rounded-md bg-ui-bg-base-pressed px-2 py-0.5 text-[12px] font-medium">
      {label}
      <button type="button" aria-label={`Remove filter ${label}`} onClick={onRemove} className="text-ui-fg-subtle hover:text-ui-fg-base">×</button>
    </span>
  )
}

/** Content → Files (Shopify layout: one list, search and filter, sort and columns, bulk delete). */
export function FilesPage() {
  const [params, setParams] = useSearchParams()
  const { pageIndex, q, setPage, setQ } = usePage(params, setParams)
  const type = params.get("type") ?? ""
  const formats = (params.get("format") ?? "").split(",").filter((f) => (FORMATS as readonly string[]).includes(f))
  const alt = params.get("alt") ?? ""
  const size = params.get("size") ?? ""
  const sort = params.get("sort") ?? "created_at"
  const dir = params.get("dir") ?? "desc"
  const { data, isLoading, error } = useFiles({ q, type, format: formats.join(","), alt, size, sort, dir, limit: PAGE, offset: pageIndex * PAGE })
  const can = useCan()
  const writable = can("content:write")
  const qc = useQueryClient()
  const del = useDeleteFiles()
  const input = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [over, setOver] = useState(false)
  const [open, setOpen] = useState<MediaFile | null>(null)
  const [sel, setSel] = useState<Set<string>>(new Set())
  const [confirm, setConfirm] = useState<string[] | null>(null)
  const [cols, setCols] = useState<Record<Col, boolean>>({ alt: true, date: true, size: true })

  const patch = (kv: Record<string, string | null>) => {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(kv)) v ? next.set(k, v) : next.delete(k)
    next.delete("page")
    setParams(next)
  }
  const toggleFormat = (f: string, on: boolean) => patch({ format: (on ? [...formats, f] : formats.filter((x) => x !== f)).join(",") || null })
  const filtered = !!(q || type || formats.length || alt || size)
  const clearAll = () => patch({ type: null, format: null, alt: null, size: null, q: null })

  const upload = async (files: File[]) => {
    if (!writable || !files.length) return
    setUploading(true)
    const { done, failed } = await uploadMany(files)
    setUploading(false)
    failed.forEach((f) => toast.error(`${f.name}: ${f.error}`))
    if (done.length) {
      toast.success(`${done.length} file${done.length === 1 ? "" : "s"} uploaded`)
      qc.invalidateQueries({ queryKey: ["files"] })
    }
  }
  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      toast.success("Link copied")
    } catch {
      toast.error("Couldn't copy the link")
    }
  }
  const name = (f: MediaFile) => (
    <div className="flex items-center gap-x-3">
      <FileThumb mime={f.mime_type} src={f.url} size={40} />
      <div className="min-w-0">
        <div className="truncate text-[13px] font-medium">{f.filename.replace(/\.[^.]*$/, "")}</div>
        <div className="text-[12px] text-ui-fg-subtle">{formatOf(f)}</div>
      </div>
    </div>
  )
  const columns = [
    { key: "name", header: "File name", cell: name },
    ...(cols.alt ? [{ key: "alt", header: "Alt text", cell: (f: MediaFile) => <span className="line-clamp-2 text-[13px] text-ui-fg-subtle">{f.alt ?? ""}</span> }] : []),
    ...(cols.date ? [{ key: "date", header: "Date added", cell: (f: MediaFile) => <span className="whitespace-nowrap text-[13px] text-ui-fg-subtle">{formatDate(f.created_at)}</span> }] : []),
    ...(cols.size ? [{ key: "size", header: "Size", cell: (f: MediaFile) => <span className="whitespace-nowrap text-[13px] text-ui-fg-subtle">{fileSize(f.size)}</span> }] : []),
    { key: "copy", header: "", cell: (f: MediaFile) => <button type="button" aria-label={`Copy link to ${f.filename}`} onClick={(e) => { e.stopPropagation(); copy(f.url) }} className="rounded p-1 text-ui-fg-subtle hover:bg-[color:var(--k-overlay-hover)]"><svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden><path d="M8.5 11.5l3-3M7 13.5l-1.2 1.2a2.5 2.5 0 01-3.5-3.5L4.5 9M13 6.5l1.2-1.2a2.5 2.5 0 013.5 3.5L15.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg></button>, className: "w-10 text-right" },
  ]

  return (
    <>
      <PageHeader title="Files" actions={writable && <Button size="small" isLoading={uploading} onClick={() => input.current?.click()}>Upload files</Button>} />
      <input ref={input} type="file" hidden multiple accept={ACCEPT} onChange={(e) => { upload(Array.from(e.target.files ?? [])); e.target.value = "" }} />
      <div onDragOver={(e) => { if (writable) { e.preventDefault(); setOver(true) } }} onDragLeave={() => setOver(false)} onDrop={(e) => { e.preventDefault(); setOver(false); upload(Array.from(e.dataTransfer.files)) }} className={over ? "rounded-xl outline-dashed outline-2 outline-ui-border-strong" : ""}>
        <Section>
          <div className="flex flex-wrap items-center gap-2 px-3 py-2">
            <span className="rounded-md bg-ui-bg-base-pressed px-2.5 py-1 text-[13px] font-medium">All</span>
            <div className="min-w-[200px] flex-1"><SearchInput value={q} onChange={setQ} placeholder="Search files" /></div>
            <DropdownMenu>
              <DropdownMenu.Trigger asChild><Button size="small" variant="secondary">Filter</Button></DropdownMenu.Trigger>
              <DropdownMenu.Content align="end" className="min-w-[200px]">
                <DropdownMenu.Label>Type</DropdownMenu.Label>
                <DropdownMenu.RadioGroup value={type} onValueChange={(v) => patch({ type: v === type ? null : v })}>
                  {Object.entries(TYPE_LABEL).map(([v, l]) => <DropdownMenu.RadioItem key={v} value={v} onSelect={(e) => { if (type === v) { e.preventDefault(); patch({ type: null }) } }}>{l}</DropdownMenu.RadioItem>)}
                </DropdownMenu.RadioGroup>
                <DropdownMenu.Separator />
                <DropdownMenu.Label>File format</DropdownMenu.Label>
                {FORMATS.map((f) => <DropdownMenu.CheckboxItem key={f} checked={formats.includes(f)} onSelect={(e) => e.preventDefault()} onCheckedChange={(c) => toggleFormat(f, c === true)}>{f.toUpperCase()}</DropdownMenu.CheckboxItem>)}
                <DropdownMenu.Separator />
                <DropdownMenu.Label>Alt text</DropdownMenu.Label>
                <DropdownMenu.RadioGroup value={alt}>
                  {Object.entries(ALT_LABEL).map(([v, l]) => <DropdownMenu.RadioItem key={v} value={v} onSelect={(e) => { e.preventDefault(); patch({ alt: alt === v ? null : v }) }}>{l}</DropdownMenu.RadioItem>)}
                </DropdownMenu.RadioGroup>
                <DropdownMenu.Separator />
                <DropdownMenu.Label>File size</DropdownMenu.Label>
                <DropdownMenu.RadioGroup value={size}>
                  {Object.entries(SIZE_LABEL).map(([v, l]) => <DropdownMenu.RadioItem key={v} value={v} onSelect={(e) => { e.preventDefault(); patch({ size: size === v ? null : v }) }}>{l}</DropdownMenu.RadioItem>)}
                </DropdownMenu.RadioGroup>
              </DropdownMenu.Content>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenu.Trigger asChild><Button size="small" variant="secondary">Sort &amp; columns</Button></DropdownMenu.Trigger>
              <DropdownMenu.Content align="end" className="min-w-[200px]">
                <DropdownMenu.Label>Sort by</DropdownMenu.Label>
                <DropdownMenu.RadioGroup value={sort} onValueChange={(v) => patch({ sort: v === "created_at" ? null : v })}>
                  {SORTS.map(([v, l]) => <DropdownMenu.RadioItem key={v} value={v}>{l}</DropdownMenu.RadioItem>)}
                </DropdownMenu.RadioGroup>
                <DropdownMenu.Separator />
                <DropdownMenu.RadioGroup value={dir} onValueChange={(v) => patch({ dir: v === "desc" ? null : v })}>
                  <DropdownMenu.RadioItem value="desc">{sort === "filename" ? "Z–A" : sort === "format" ? "Format Z–A" : "Newest / largest first"}</DropdownMenu.RadioItem>
                  <DropdownMenu.RadioItem value="asc">{sort === "filename" ? "A–Z" : sort === "format" ? "Format A–Z" : "Oldest / smallest first"}</DropdownMenu.RadioItem>
                </DropdownMenu.RadioGroup>
                <DropdownMenu.Separator />
                <DropdownMenu.Label>Columns</DropdownMenu.Label>
                {COLS.map(([k, l]) => <DropdownMenu.CheckboxItem key={k} checked={cols[k]} onSelect={(e) => e.preventDefault()} onCheckedChange={(c) => setCols((x) => ({ ...x, [k]: c === true }))}>{l}</DropdownMenu.CheckboxItem>)}
              </DropdownMenu.Content>
            </DropdownMenu>
          </div>
          {filtered && (
            <div className="flex flex-wrap items-center gap-2 px-3 pb-2">
              {type && <Chip label={`Type is ${TYPE_LABEL[type]}`} onRemove={() => patch({ type: null })} />}
              {formats.map((f) => <Chip key={f} label={`Format is ${f.toUpperCase()}`} onRemove={() => toggleFormat(f, false)} />)}
              {alt && <Chip label={ALT_LABEL[alt]} onRemove={() => patch({ alt: null })} />}
              {size && <Chip label={`Size: ${SIZE_LABEL[size]}`} onRemove={() => patch({ size: null })} />}
              <button type="button" className="text-[12px] text-ui-fg-subtle underline" onClick={clearAll}>Clear all</button>
            </div>
          )}
          {error ? (
            <ErrorState error={error} />
          ) : (
            <DataTable
              rowKey={(f: MediaFile) => f.id}
              rows={data?.files}
              count={data?.count ?? 0}
              pageSize={PAGE}
              pageIndex={pageIndex}
              onPageChange={setPage}
              loading={isLoading}
              onRowClick={setOpen}
              columns={columns}
              selected={writable ? sel : undefined}
              onSelectedChange={writable ? setSel : undefined}
              bulkActions={writable ? <Button size="small" variant="secondary" onClick={() => setConfirm([...sel])}>Delete files</Button> : undefined}
              empty={
                <EmptyState
                  title={filtered ? "No files match" : "Upload and manage your files"}
                  body={filtered ? undefined : writable ? "Drop images, videos or PDFs here, or use Upload files. Up to 20 MB each." : undefined}
                  action={writable && !filtered ? <Button size="small" variant="secondary" onClick={() => input.current?.click()}>Upload files</Button> : undefined}
                />
              }
              mobile={(f: MediaFile) => (
                <div className="flex items-center gap-x-3">
                  <FileThumb mime={f.mime_type} src={f.url} size={44} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-medium">{f.filename.replace(/\.[^.]*$/, "")}</div>
                    <div className="text-[12px] text-ui-fg-subtle">{formatOf(f)} · {fileSize(f.size)} · {formatDate(f.created_at)}</div>
                  </div>
                </div>
              )}
            />
          )}
        </Section>
      </div>

      {open && <FileDrawer file={open} writable={writable} onClose={() => setOpen(null)} onDelete={() => setConfirm([open.id])} />}

      <Prompt open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Delete {confirm?.length === 1 ? "this file" : `${confirm?.length} files`}?</Prompt.Title>
            <Prompt.Description>This removes the file permanently. Any page or product still using it will lose the image.</Prompt.Description>
          </Prompt.Header>
          <div className="px-6 pb-2">{confirm && <UsageList ids={confirm.slice(0, 50)} />}</div>
          <Prompt.Footer>
            <Prompt.Cancel>Cancel</Prompt.Cancel>
            <Prompt.Action
              onClick={() => {
                const ids = confirm!
                del.mutate(ids, {
                  onSuccess: (r) => {
                    r.failed ? toast.error(`${r.failed} couldn't be deleted`) : toast.success(`${r.deleted} deleted`)
                    setSel((s) => new Set([...s].filter((x) => !ids.includes(x))))
                    setOpen(null)
                    setConfirm(null)
                  },
                })
              }}
            >
              Delete
            </Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </>
  )
}
